import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Briefcase, Building2, LogOut, FileSpreadsheet, Search } from "lucide-react";
import consApi from "../../consultantApi";
import { Brand } from "../../components/Brand";
import { DueDiligenceDialog } from "../../components/dd/DueDiligenceDialog";
import { CONTRACT_TYPES } from "../admin/DdEmployeeFields";

const TYPE_LABEL = Object.fromEntries(CONTRACT_TYPES.map((t) => [t.v, t.l]));
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");

const CompanyList = ({ companies, selected, onSelect }) => (
  <aside className="bg-white border border-gray-200 rounded-lg p-2 h-fit" data-testid="consulente-companies">
    <div className="px-2 py-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-400">Aziende clienti</div>
    {companies.length === 0 && <p className="px-2 py-3 text-[13px] text-gray-400">Nessuna azienda assegnata.</p>}
    {companies.map((c) => (
      <button key={c.code} onClick={() => onSelect(c.code)} data-testid={`consulente-company-${c.code}`}
        className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-md text-[13.5px] font-semibold transition-colors ${selected === c.code ? "bg-[#16345c] text-white" : "text-[#16345c] hover:bg-[#eaf1f8]"}`}>
        <Building2 size={15} className="shrink-0" /> <span className="truncate">{c.label}</span>
      </button>
    ))}
  </aside>
);

const EmployeesTable = ({ employees }) => (
  <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
    <table className="w-full min-w-[640px] text-[13px]" data-testid="consulente-employees-table">
      <thead>
        <tr className="bg-[#16345c] text-white text-[12px] uppercase tracking-wide">
          <th className="text-left px-4 py-3">Dipendente</th><th className="text-left px-4 py-3">Qualifica</th>
          <th className="text-left px-4 py-3">Tipo rapporto</th><th className="text-left px-4 py-3">Assunzione</th><th className="text-left px-4 py-3">Stato</th>
        </tr>
      </thead>
      <tbody>
        {employees.map((e) => (
          <tr key={e.id} className="border-t border-gray-100" data-testid={`consulente-emp-${e.id}`}>
            <td className="px-4 py-2.5"><div className="font-bold text-[#16345c]">{e.name}</div><div className="text-[11px] text-gray-400">{e.matricola}</div></td>
            <td className="px-4 py-2.5 text-gray-600">{e.job_title || "—"}</td>
            <td className="px-4 py-2.5 text-gray-600">{TYPE_LABEL[e.contract_type] || "—"}{e.contract_type_inferred && <span className="text-[11px] text-amber-600"> (dedotto)</span>}</td>
            <td className="px-4 py-2.5 text-gray-600">{fmtDate(e.hire_date)}</td>
            <td className="px-4 py-2.5">{e.active === false ? <span className="text-gray-400">Cessato {e.termination_date ? fmtDate(e.termination_date) : ""}</span> : <span className="text-[#009640] font-semibold">Attivo</span>}</td>
          </tr>
        ))}
        {employees.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400">Nessun dipendente trovato</td></tr>}
      </tbody>
    </table>
  </div>
);

// Area Consulente: aziende clienti assegnate → organico → Export Due Diligence.
const ConsulenteDashboard = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [showDd, setShowDd] = useState(false);

  useEffect(() => {
    consApi.get("/consulente/me").then((r) => {
      setMe(r.data);
      if (r.data.companies?.length) setSelected(r.data.companies[0].code);
    }).catch(() => navigate("/consulente/login", { replace: true }));
  }, [navigate]);

  useEffect(() => {
    if (!selected) return;
    setData(null);
    consApi.get(`/consulente/companies/${selected}/employees`).then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Organico non disponibile"));
  }, [selected]);

  const logout = async () => {
    try { await consApi.post("/consulente/auth/logout"); } catch { /* sessione già chiusa */ }
    navigate("/consulente/login", { replace: true });
  };

  if (!me) return <div className="min-h-screen bg-[#f4f6f9] flex items-center justify-center text-gray-400">Caricamento…</div>;
  const employees = (data?.employees || []).filter((e) => `${e.name} ${e.matricola} ${e.job_title}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="min-h-screen bg-[#f4f6f9]" data-testid="consulente-dashboard">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4"><Brand dark={false} className="text-[20px]" /><span className="hidden sm:flex items-center gap-1.5 text-[13px] font-bold text-[#16345c]"><Briefcase size={15} /> Area Consulente</span></div>
          <div className="flex items-center gap-3">
            <span className="text-[13px] text-gray-500 hidden sm:inline" data-testid="consulente-name">{me.consultant.name}</span>
            <button onClick={logout} data-testid="consulente-logout" className="flex items-center gap-1.5 text-[13px] font-semibold text-gray-600 border border-gray-300 hover:border-[#16345c] px-3 py-1.5 rounded-sm"><LogOut size={14} /> Esci</button>
          </div>
        </div>
      </header>
      <main className="max-w-[1200px] mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6">
        <CompanyList companies={me.companies} selected={selected} onSelect={setSelected} />
        <section>
          {selected && (
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <h1 className="text-[20px] font-black text-[#16345c]" data-testid="consulente-company-title">{data?.company?.label || "…"}</h1>
              <div className="flex items-center gap-2">
                <div className="flex items-center border border-gray-300 rounded-sm px-3 bg-white"><Search size={15} className="text-gray-400" />
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca dipendente..." data-testid="consulente-search" className="py-2 px-2 text-[14px] outline-none w-[180px]" />
                </div>
                <button onClick={() => setShowDd(true)} data-testid="dd-export-btn"
                  className="flex items-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors"><FileSpreadsheet size={16} /> Esporta per Due Diligence</button>
              </div>
            </div>
          )}
          {selected && (data ? <EmployeesTable employees={employees} /> : <div className="text-gray-400 text-[13px]">Caricamento organico…</div>)}
        </section>
      </main>
      {selected && (
        <DueDiligenceDialog open={showDd} onClose={() => setShowDd(false)} client={consApi}
          basePath={`/consulente/companies/${selected}/dd`} companyLabel={data?.company?.label} />
      )}
    </div>
  );
};

export default ConsulenteDashboard;
